import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  NgModule,
  OnInit,
  Optional,
  Self,
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'app-checkbox-input',
  templateUrl: './checkbox-input.component.html',
  styleUrls: ['./checkbox-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckboxInputComponent implements ControlValueAccessor, OnInit {
  private static Counter = 0;

  // tslint:disable-next-line bool-param-default
  set value(val: boolean | undefined) {
    if (val !== undefined && this._value !== val) {
      this._value = val;
      this.onChange(val);
      this.onTouch();
    }
  }

  @HostBinding('attr.checked')
  _value = false;

  @HostBinding('attr.invalid')
  get invalid(): boolean {
    if (this.forceError) {
      return !!this.forceError;
    }

    if (this.form) {
      return !!this.form.submitted && !!this.control.invalid;
    }

    if (this.ngForm) {
      return !!this.ngForm.submitted && !!this.control.invalid;
    }

    return false;
  }

  @Input() forceError = false;

  readonly id = `checkbox-input-${++CheckboxInputComponent.Counter}`;

  constructor(
    readonly element: ElementRef,
    private readonly cdr: ChangeDetectorRef,
    @Optional() private readonly form: FormGroupDirective,
    @Optional() private readonly ngForm: NgForm,
    @Self() @Optional() readonly control: NgControl
  ) {
    if (this.control) {
      this.control.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    if (this.form) {
      this.form.ngSubmit.pipe(untilDestroyed(this)).subscribe({ next: () => this.cdr.detectChanges() });
    }

    if (this.ngForm) {
      this.ngForm.ngSubmit.pipe(untilDestroyed(this)).subscribe({ next: () => this.cdr.detectChanges() });
    }
  }

  onChange: (_: boolean) => void = () => {
    // intentional empty arrow function
  };
  onTouch: () => void = () => {
    // intentional empty arrow function
  };

  @HostListener('click', ['$event'])
  onClick(event: any): void {
    this.changeValue(event);
  }

  @HostListener('keydown.enter', ['$event'])
  onKeydownEnter(event: any): void {
    this.changeValue(event);
  }

  private changeValue(event: any) {
    event.preventDefault();

    this.writeValue(!this._value);
    this.onChange(this._value);
  }

  writeValue(obj: boolean): void {
    this._value = obj;
    this.cdr.detectChanges();
  }

  registerOnChange(fn: any): void {
    this.onChange = (value: boolean) => {
      fn(value);
      this.cdr.detectChanges();
    };
  }

  registerOnTouched(fn: any): void {
    this.onTouch = () => {
      fn();
      this.cdr.detectChanges();
    };
  }
}

@NgModule({
  imports: [CommonModule],
  declarations: [CheckboxInputComponent],
  exports: [CheckboxInputComponent],
})
export class CheckBoxInputModule {}
